"use client";

import { useState } from "react";
import Link from "next/link";
import { useLang } from "@/contexts/language-context";

// ── Bilingual helper type ──────────────────────────────────────────────────────

type B = { en: string; es: string };
type Vertical = "restaurant" | "nightlife";

// ── Brand gradients (kept as inline styles — brand identity) ───────────────────

const GRADIENT_RESTAURANT = "linear-gradient(135deg, #D7263D 0%, #F4722B 100%)";
const GRADIENT_NIGHTLIFE = "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)";
const GRADIENT_NIGHTLIFE_PRO = "linear-gradient(135deg, #7C3AED 0%, #4C1D95 100%)";
const GRADIENT_FREE = "linear-gradient(135deg, #F7F5F1 0%, #EAE7E1 100%)";
// Shares its warm end (#F4722B) with GRADIENT_RESTAURANT on purpose — Plus reads
// as "on the way to Pro," never as a competing flagship. isPrimary stays false
// for this plan (see RESTAURANT_PLANS below) so the primary badge, the
// heaviest shadow and the boldest red stay exclusive to Pro.
const GRADIENT_PLUS = "linear-gradient(135deg, #F2A93B 0%, #F4722B 100%)";

// ── Savings Calculator ────────────────────────────────────────────────────────

function SavingsCalculator() {
  const { t, lang } = useLang();
  const [monthlySales, setMonthlySales] = useState(25000);
  const [cardPct, setCardPct] = useState(70);

  const cardSales = (monthlySales * cardPct) / 100;
  // Example assumption (labeled in the UI): 3.2% effective card rate.
  const currentFees = cardSales * 0.032;
  const withRedy = 0; // dual pricing — the card price is designed to cover the processing cost
  const proPlanCost = 97;
  const netSavings = currentFees - proPlanCost;

  const fmt = (n: number) =>
    n.toLocaleString(lang === "es" ? "es-MX" : "en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  return (
    <div className="bg-white rounded-3xl border border-black/5 p-8 max-w-xl mx-auto shadow-sm">
      <h3 className="text-[18px] font-black text-redy-charcoal mb-1">
        {t("How much are you losing in fees?", "¿Cuánto estás perdiendo en fees?")}
      </h3>
      <p className="text-[13px] text-redy-secondary mb-7">
        {t(
          "With Redy's dual pricing, the card price is designed to cover the processing cost, so card fees don't come out of your margin.",
          "Con el dual pricing de Redy, el precio con tarjeta está pensado para cubrir el costo de procesamiento, así los fees no salen de tu margen."
        )}
      </p>

      {/* Monthly sales slider */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[13px] font-semibold text-redy-charcoal">
            {t("Monthly sales", "Ventas mensuales")}
          </label>
          <span className="text-[15px] font-black text-redy-charcoal">{fmt(monthlySales)}</span>
        </div>
        <input
          type="range"
          min={5000}
          max={200000}
          step={1000}
          value={monthlySales}
          onChange={(e) => setMonthlySales(Number(e.target.value))}
          className="w-full accent-redy-red"
        />
        <div className="flex justify-between text-[11px] text-redy-secondary mt-1">
          <span>$5K</span>
          <span>$200K</span>
        </div>
      </div>

      {/* Card % slider */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[13px] font-semibold text-redy-charcoal">
            {t("% paying with a card", "% que paga con tarjeta")}
          </label>
          <span className="text-[15px] font-black text-redy-charcoal">{cardPct}%</span>
        </div>
        <input
          type="range"
          min={10}
          max={100}
          step={5}
          value={cardPct}
          onChange={(e) => setCardPct(Number(e.target.value))}
          className="w-full accent-redy-red"
        />
        <div className="flex justify-between text-[11px] text-redy-secondary mt-1">
          <span>10%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Results */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center justify-between bg-red-50 rounded-2xl px-4 py-3">
          <span className="text-[13px] text-red-800">
            {t("Typical card fees (example: 3.2%)", "Fees de tarjeta típicos (ejemplo: 3.2%)")}
          </span>
          <span className="text-[15px] font-black text-red-800">
            {fmt(currentFees)}
            {t("/mo", "/mes")}
          </span>
        </div>
        <div className="flex items-center justify-between bg-redy-warm-white rounded-2xl px-4 py-3">
          <span className="text-[13px] text-redy-secondary">
            {t("Out of your margin with dual pricing", "De tu margen con dual pricing")}
          </span>
          <span className="text-[15px] font-black text-redy-secondary">
            {fmt(withRedy)}
            {t("/mo", "/mes")}
          </span>
        </div>
        <div className="flex items-center justify-between bg-redy-warm-white rounded-2xl px-4 py-3">
          <span className="text-[13px] text-redy-secondary">
            {t("Redy Pro cost", "Costo Redy Pro")}
          </span>
          <span className="text-[15px] font-black text-redy-secondary">
            -{fmt(proPlanCost)}
            {t("/mo", "/mes")}
          </span>
        </div>
        <div
          className="flex items-center justify-between rounded-2xl px-4 py-4"
          style={{ background: netSavings > 0 ? GRADIENT_RESTAURANT : "#F7F5F1" }}
        >
          <span
            className={`text-[14px] font-bold ${
              netSavings > 0 ? "text-white" : "text-redy-secondary"
            }`}
          >
            {netSavings > 0
              ? t("✦ Net monthly savings", "✦ Ahorro neto mensual")
              : t("Net monthly savings", "Ahorro neto mensual")}
          </span>
          <span
            className={`text-[20px] font-black ${
              netSavings > 0 ? "text-white" : "text-redy-secondary"
            }`}
          >
            {fmt(Math.max(0, netSavings))}
            {t("/mo", "/mes")}
          </span>
        </div>
      </div>

      {netSavings > 0 && (
        <p className="text-center text-[13px] text-redy-secondary">
          {t("Per year:", "Al año:")}{" "}
          <span className="font-black text-redy-red text-[15px]">{fmt(netSavings * 12)}</span>{" "}
          {t("net savings", "de ahorro neto")}
        </p>
      )}
      <p className="text-center text-[11px] text-redy-secondary mt-3">
        {t(
          "Example estimate. Assumes a 3.2% effective card rate and the Pro plan; your actual rates and savings will vary.",
          "Estimación de ejemplo. Supone una tasa efectiva de 3.2% y el plan Pro; tus tasas y ahorro reales pueden variar."
        )}
      </p>
    </div>
  );
}

// ── Plan Data ─────────────────────────────────────────────────────────────────

interface Plan {
  id: string;
  name: string;
  price: string;
  priceNote: B;
  gradient: string;
  accent: string;
  badge?: B;
  description: B;
  features: B[];
  cta: B;
  /**
   * Los planes de pago apuntan a /signup, NO a /checkout. Ese checkout esta
   * muerto: devuelve 503 sin NORTH_SUBSCRIPTION_MID y, aun cobrando, tiene un
   * TODO(subscription-activation) — no crea la suscripcion ni provisiona la
   * cuenta. La captura que SI funciona vive en /[slug]/upgrade, dentro del
   * panel, asi que el prospecto se registra gratis y contrata desde adentro.
   */
  ctaHref: string;
  isPrimary: boolean;
}

const RESTAURANT_PLANS: Plan[] = [
  {
    id: "restaurant_free",
    name: "Redy Start",
    price: "$0",
    priceNote: { en: "/ mo · with Redy payments", es: "/ mes · con pagos de Redy" },
    gradient: GRADIENT_FREE,
    accent: "#9B9690",
    description: {
      en: "Free software plan when you process payments with Redy (activation subject to payments account approval).",
      es: "Plan de software gratis si procesas tus pagos con Redy (la activación depende de la aprobación de tu cuenta de pagos).",
    },
    features: [
      { en: "1 POS terminal · 1 KDS · 1 kiosk", es: "1 terminal POS · 1 KDS · 1 kiosko" },
      { en: "Cash and card payments", es: "Cobro efectivo y tarjeta" },
      { en: "Unlimited menu with photos and modifiers", es: "Menú ilimitado con fotos y modificadores" },
      { en: "Online ordering with QR", es: "Ordenamiento online con QR" },
      { en: "Receipts (print, SMS, email)", es: "Recibos (print, SMS, email)" },
      { en: "Daily dashboard + Z Report", es: "Dashboard del día + Z Report" },
      { en: "Up to 3 employees with PIN", es: "Hasta 3 empleados con PIN" },
    ],
    cta: { en: "Start free", es: "Empezar gratis" },
    ctaHref: "/signup",
    isPrimary: false,
  },
  {
    id: "restaurant_plus",
    name: "Redy Plus",
    price: "$49",
    priceNote: { en: "/ mo · 3 POS, 10 employees", es: "/ mes · 3 POS, 10 empleados" },
    gradient: GRADIENT_PLUS,
    accent: "#F2A93B",
    badge: { en: "Room to grow", es: "Para crecer" },
    description: {
      en: "Raise the growth caps. Add payroll and your own brand.",
      es: "Sube los topes de crecimiento. Suma nómina y marca propia.",
    },
    features: [
      { en: "Everything in Start, plus:", es: "Todo de Start, más:" },
      { en: "✦ 3 POS, 2 KDS, 10 employees", es: "✦ 3 POS, 2 KDS, 10 empleados" },
      { en: "Unlimited CRM, loyalty, reservations and reviews", es: "CRM, lealtad, reservas y reseñas sin límite" },
      { en: "Payroll for your team", es: "Nómina para tu equipo" },
      { en: "Employee roles and self-service portal", es: "Roles de empleado y portal de autoservicio" },
      { en: "Custom domain for your website", es: "Dominio propio para tu página web" },
      { en: "Custom themes and branding for your page", es: "Temas y marca personalizada para tu página" },
    ],
    cta: { en: "Get Plus", es: "Contratar Plus" },
    ctaHref: "/signup?plan=restaurant_plus",
    isPrimary: false,
  },
  {
    id: "restaurant_pro",
    name: "Redy Pro",
    price: "$97",
    priceNote: { en: "/ mo · unlimited devices", es: "/ mes · dispositivos ilimitados" },
    gradient: GRADIENT_RESTAURANT,
    accent: "#D7263D",
    badge: { en: "Unlimited devices", es: "Dispositivos ilimitados" },
    description: {
      en: "Run your whole operation from one place.",
      es: "Gestiona toda tu operación en un solo lugar.",
    },
    features: [
      { en: "Everything in Start, plus:", es: "Todo de Start, más:" },
      { en: "✦ Unlimited POS, KDS and kiosks", es: "✦ POS, KDS y kioskos ilimitados" },
      { en: "Full inventory (stock, COGS, alerts)", es: "Inventario completo (stock, COGS, alertas)" },
      { en: "Payroll + Excel export", es: "Payroll + export Excel" },
      { en: "Advanced reports + CSV/XLSX", es: "Reportes avanzados + CSV/XLSX" },
      { en: "Loyalty program and CRM", es: "Programa de lealtad y CRM" },
      { en: "Unlimited employees with roles", es: "Empleados ilimitados con roles" },
      { en: "Public website + Menu Board", es: "Página web pública + Menu Board" },
    ],
    cta: { en: "Get Pro", es: "Contratar Pro" },
    ctaHref: "/signup?plan=restaurant_pro",
    isPrimary: true,
  },
];

const NIGHTLIFE_PLANS: Plan[] = [
  {
    id: "nightlife",
    name: "Redy Nightlife",
    price: "$147",
    priceNote: { en: "/ mo", es: "/ mes" },
    gradient: GRADIENT_NIGHTLIFE,
    accent: "#7C3AED",
    description: {
      en: "The smart POS for bars and nightclubs.",
      es: "El POS inteligente para bares y nightclubs.",
    },
    features: [
      { en: "Smart VIP table map (live)", es: "Mapa VIP inteligente (en vivo)" },
      { en: "QR door check-in", es: "Check-in QR en la puerta" },
      { en: "Public venue microsite", es: "Microsite público del venue" },
      { en: "Card processing with dual pricing built in", es: "Procesamiento de tarjeta con dual pricing incorporado" },
      { en: "Guest list management", es: "Gestión de guest list" },
      { en: "Event ticketing", es: "Venta de tickets" },
    ],
    cta: { en: "Get Nightlife", es: "Contratar Nightlife" },
    ctaHref: "/signup?plan=nightlife",
    isPrimary: false,
  },
  {
    id: "nightlife_pro",
    name: "Redy Nightlife PRO",
    price: "$197",
    priceNote: { en: "/ mo · unlimited devices", es: "/ mes · dispositivos ilimitados" },
    gradient: GRADIENT_NIGHTLIFE_PRO,
    accent: "#7C3AED",
    badge: { en: "For established venues", es: "Para venues establecidos" },
    description: {
      en: "The full suite for venues and clubs.",
      es: "La suite completa para venues y clubes.",
    },
    features: [
      { en: "Everything in Nightlife, plus:", es: "Todo de Nightlife, más:" },
      { en: "Live bar tabs with realtime sync", es: "Tabs de barra en vivo con sync" },
      { en: "Promoter links + commission tracking", es: "Links de promotores + control de comisiones" },
      { en: "Bottle service preset packages", es: "Paquetes preset de bottle service" },
      { en: "Realtime dashboard per night", es: "Dashboard realtime por noche" },
      { en: "Historical reports + cohorts", es: "Reportes históricos + cohortes" },
      { en: "Custom domain", es: "Dominio personalizado" },
      { en: "Priority support", es: "Soporte prioritario" },
    ],
    cta: { en: "Get Nightlife PRO", es: "Contratar Nightlife PRO" },
    ctaHref: "/signup?plan=nightlife_pro",
    isPrimary: true,
  },
];

// ── Feature comparison table ───────────────────────────────────────────────────

interface CompareRow {
  feature: B;
  free: B | string;
  plus: B | string;
  pro: B | string;
  nightlife: B | string;
  nightlife_pro: B | string;
}

const UNLIMITED: B = { en: "Unlimited", es: "Ilimitados" };
const MAX3: B = { en: "3 max", es: "3 máx" };
const MAX10: B = { en: "10 max", es: "10 máx" };

// Plus column verified against lib/billing/entitlements.ts (restaurant_plus
// table) — inventory and advanced reports stay "—" through Plus on purpose:
// both are Pro-only in FEATURE_PLAN_MAP, and Plus must not look like it
// includes them or it cannibalizes the Pro upsell.
const COMPARE_ROWS: CompareRow[] = [
  { feature: { en: "POS terminal", es: "Terminal POS" }, free: "1", plus: "3", pro: UNLIMITED, nightlife: "—", nightlife_pro: "—" },
  { feature: { en: "Kitchen Display (KDS)", es: "KDS / pantallas cocina" }, free: "1", plus: "2", pro: UNLIMITED, nightlife: "—", nightlife_pro: "—" },
  { feature: { en: "Full inventory", es: "Inventario completo" }, free: "—", plus: "—", pro: "✓", nightlife: "—", nightlife_pro: "—" },
  { feature: { en: "Payroll", es: "Payroll + nómina" }, free: "—", plus: "✓", pro: "✓", nightlife: "—", nightlife_pro: "—" },
  { feature: { en: "Loyalty program", es: "Programa de lealtad" }, free: "—", plus: "✓", pro: "✓", nightlife: "—", nightlife_pro: "—" },
  { feature: { en: "Advanced reports", es: "Reportes avanzados" }, free: "—", plus: "—", pro: "✓", nightlife: "—", nightlife_pro: "—" },
  { feature: { en: "Unlimited employees", es: "Empleados ilimitados" }, free: MAX3, plus: MAX10, pro: "✓", nightlife: "✓", nightlife_pro: "✓" },
  { feature: { en: "Smart VIP table map", es: "Mapa VIP inteligente" }, free: "—", plus: "—", pro: "—", nightlife: "✓", nightlife_pro: "✓" },
  { feature: { en: "Live bar tabs", es: "Tabs de barra en vivo" }, free: "—", plus: "—", pro: "—", nightlife: "—", nightlife_pro: "✓" },
  { feature: { en: "QR door check-in", es: "Check-in QR en puerta" }, free: "—", plus: "—", pro: "—", nightlife: "✓", nightlife_pro: "✓" },
  // Ticketing es idéntico en ambos planes nightlife (decisión de Samuel
  // 2026-08-27): el tope de eventos nunca existió en entitlements y el gate de
  // tiers nunca se cableó, así que se retiró el claim en vez de inventar el
  // tope — el upsell del PRO va por tabs/promotores/kiosko/dashboard/reportes.
  { feature: { en: "Event ticketing", es: "Venta de tickets" }, free: "—", plus: "—", pro: "—", nightlife: "✓", nightlife_pro: "✓" },
  { feature: { en: "Promoter links & commissions", es: "Links y comisiones de promotores" }, free: "—", plus: "—", pro: "—", nightlife: "—", nightlife_pro: "✓" },
  { feature: { en: "Realtime dashboard", es: "Dashboard realtime" }, free: "—", plus: "—", pro: "—", nightlife: "—", nightlife_pro: "✓" },
];

// ── PlanCard ──────────────────────────────────────────────────────────────────

function PlanCard({ plan }: { plan: Plan }) {
  const { t } = useLang();
  const isFreeStyle = plan.id === "restaurant_free";
  return (
    <div
      className={`relative flex flex-col rounded-3xl overflow-hidden ${
        plan.isPrimary ? "shadow-2xl" : "shadow-sm border border-black/5"
      }`}
    >
      {plan.badge && (
        <div
          className="absolute top-4 right-4 text-[11px] font-black uppercase tracking-widest px-3 py-1 rounded-full text-white z-10"
          style={{ background: plan.accent }}
        >
          {t(plan.badge.en, plan.badge.es)}
        </div>
      )}
      <div
        className={`px-6 pt-7 pb-6 ${isFreeStyle ? "bg-redy-warm-white" : "text-white"}`}
        style={!isFreeStyle ? { background: plan.gradient } : undefined}
      >
        <p
          className={`text-[11px] font-black uppercase tracking-widest mb-1 ${
            isFreeStyle ? "text-redy-secondary" : "text-white/70"
          }`}
        >
          {plan.name}
        </p>
        <div className="flex items-end gap-2 mb-2">
          <span
            className={`text-[38px] font-black leading-none ${
              isFreeStyle ? "text-redy-charcoal" : "text-white"
            }`}
          >
            {plan.price}
          </span>
          <span
            className={`text-[12px] mb-1.5 ${isFreeStyle ? "text-redy-secondary" : "text-white/70"}`}
          >
            {t(plan.priceNote.en, plan.priceNote.es)}
          </span>
        </div>
        <p
          className={`text-[13px] ${isFreeStyle ? "text-redy-secondary" : "text-white/80"}`}
        >
          {t(plan.description.en, plan.description.es)}
        </p>
      </div>
      <div className="flex-1 bg-white px-6 py-5 flex flex-col">
        <ul className="space-y-2.5 flex-1 mb-6">
          {plan.features.map((f, i) => {
            const isDivider = f.en.endsWith("plus:") || f.es.endsWith("más:");
            return (
              <li
                key={i}
                className={`flex items-start gap-2.5 text-[13px] ${
                  isDivider ? "text-redy-secondary font-semibold pt-1" : "text-redy-charcoal"
                }`}
              >
                {!isDivider && (
                  <span
                    className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[9px] font-bold"
                    style={{ background: isFreeStyle ? "#9B9690" : plan.accent }}
                  >
                    ✓
                  </span>
                )}
                {isDivider && <span className="w-4 flex-shrink-0" />}
                {t(f.en, f.es)}
              </li>
            );
          })}
        </ul>
        <a
          href={plan.ctaHref}
          className="block py-3.5 rounded-2xl text-center text-[14px] font-black transition-opacity hover:opacity-90"
          style={
            plan.isPrimary
              ? { background: plan.gradient, color: "white" }
              : {
                  background: "#F7F5F1",
                  color: "#1A1A1A",
                  border: "1px solid rgba(0,0,0,0.05)",
                }
          }
        >
          {t(plan.cta.en, plan.cta.es)} →
        </a>
      </div>
    </div>
  );
}

// ── Compare cell render helper ─────────────────────────────────────────────────

function CompareCell({ value, t }: { value: B | string; t: (en: string, es: string) => string }) {
  const text = typeof value === "string" ? value : t(value.en, value.es);
  if (text === "✓") {
    return <span className="text-redy-green font-bold">✓</span>;
  }
  if (text === "—") {
    return <span className="text-redy-charcoal/20">—</span>;
  }
  return <span className="text-redy-charcoal font-semibold">{text}</span>;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PricingClient() {
  const { t } = useLang();
  const [vertical, setVertical] = useState<Vertical>("restaurant");
  const plans = vertical === "restaurant" ? RESTAURANT_PLANS : NIGHTLIFE_PLANS;

  return (
    <div className="bg-redy-warm-white">
      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 pt-32 pb-12 text-center">
        <div
          className="inline-block text-[11px] font-black uppercase tracking-[0.15em] px-4 py-1.5 rounded-full text-white mb-6"
          style={{ background: GRADIENT_RESTAURANT }}
        >
          {t("Transparent pricing", "Precios transparentes")}
        </div>
        <h1 className="text-[44px] sm:text-[60px] font-black text-redy-charcoal leading-[1.0] mb-5">
          {t("One free plan.", "Un plan gratis.")}
          <br />
          <span
            style={{
              background: GRADIENT_RESTAURANT,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {t("Two verticals.", "Dos verticales.")}
          </span>
        </h1>
        <p className="text-[17px] text-redy-secondary max-w-xl mx-auto leading-relaxed mb-10">
          {t(
            "Start free for restaurants and food trucks when you process payments with Redy. Add Plus or Pro as you grow. Run a bar or nightclub? Switch to Nightlife. No credit card to start; month to month, no long-term contract.",
            "Empieza gratis para restaurantes y food trucks si procesas tus pagos con Redy. Suma Plus o Pro conforme creces. ¿Bar o nightclub? Cambia a Nightlife. Sin tarjeta de crédito para empezar; mes a mes, sin contrato a largo plazo."
          )}
        </p>

        {/* Vertical toggle */}
        <div className="inline-flex items-center bg-white border border-black/5 rounded-2xl p-1 shadow-sm">
          {(["restaurant", "nightlife"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setVertical(v)}
              className={`px-6 py-2.5 rounded-xl text-[14px] font-bold transition-all duration-150 cursor-pointer ${
                vertical === v ? "text-white shadow" : "text-redy-secondary hover:text-redy-charcoal"
              }`}
              style={
                vertical === v
                  ? {
                      background:
                        v === "restaurant" ? GRADIENT_RESTAURANT : GRADIENT_NIGHTLIFE,
                    }
                  : undefined
              }
            >
              {v === "restaurant"
                ? t("🍽 Restaurant", "🍽 Restaurante")
                : t("🌃 Nightlife", "🌃 Nightlife")}
            </button>
          ))}
        </div>
      </div>

      {/* Plans grid — restaurant renders 3 cards (Start/Plus/Pro), nightlife renders
          2 (Nightlife/Nightlife PRO). md:grid-cols-2 is unchanged/shared so nightlife's
          layout is untouched; lg:grid-cols-3 only kicks in for the restaurant vertical. */}
      <div className="max-w-5xl mx-auto px-4 pb-20">
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${
            vertical === "restaurant" ? "lg:grid-cols-3" : ""
          }`}
        >
          {plans.map((p) => (
            <PlanCard key={p.id} plan={p} />
          ))}
        </div>
      </div>

      {/* Savings calculator (restaurant only) */}
      {vertical === "restaurant" && (
        <div className="max-w-5xl mx-auto px-4 pb-24">
          <div className="text-center mb-10">
            <h2 className="text-[28px] font-black text-redy-charcoal mb-2">
              {t("Estimate your savings", "Estima tu ahorro")}
            </h2>
            <p className="text-[15px] text-redy-secondary">
              {t(
                "With dual pricing, card payers cover the processing cost",
                "Con dual pricing, quien paga con tarjeta cubre el costo de procesamiento"
              )}
            </p>
          </div>
          <SavingsCalculator />
        </div>
      )}

      {/* Feature comparison table */}
      <div className="max-w-5xl mx-auto px-4 pb-24">
        <h2 className="text-[24px] font-black text-redy-charcoal mb-8 text-center">
          {t("Full comparison", "Comparativa completa")}
        </h2>
        <div className="bg-white rounded-3xl border border-black/5 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-black/5">
                  <th className="text-left px-6 py-4 font-semibold text-redy-secondary w-1/3">
                    {t("Feature", "Feature")}
                  </th>
                  <th className="px-4 py-4 font-black text-redy-secondary text-center">
                    Start
                    <br />
                    <span className="text-[11px] font-normal">$0</span>
                  </th>
                  <th className="px-4 py-4 font-black text-redy-secondary text-center">
                    Plus
                    <br />
                    <span className="text-[11px] font-normal">$49</span>
                  </th>
                  <th
                    className="px-4 py-4 text-center"
                    style={{
                      background: GRADIENT_RESTAURANT,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    <span className="font-black block">Pro</span>
                    <span className="text-[11px] font-normal text-redy-secondary">$97</span>
                  </th>
                  <th
                    className="px-4 py-4 font-black text-center"
                    style={{
                      background: GRADIENT_NIGHTLIFE,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    <span className="font-black block">Nightlife</span>
                    <span className="text-[11px] font-normal text-redy-secondary">$147</span>
                  </th>
                  <th
                    className="px-4 py-4 font-black text-center"
                    style={{
                      background: GRADIENT_NIGHTLIFE_PRO,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    <span className="font-black block">NL PRO</span>
                    <span className="text-[11px] font-normal text-redy-secondary">$197</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}>
                    <td className="px-6 py-3.5 text-redy-charcoal font-medium">
                      {t(row.feature.en, row.feature.es)}
                    </td>
                    {[row.free, row.plus, row.pro, row.nightlife, row.nightlife_pro].map((val, j) => (
                      <td key={j} className="px-4 py-3.5 text-center">
                        <CompareCell value={val} t={t} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Questions CTA — kept as conversion gesture; MarketingFooter renders brand/legal below */}
      <div className="bg-white border-t border-black/5 py-16 text-center px-4">
        <h2 className="text-[28px] font-black text-redy-charcoal mb-3">
          {t("Got questions?", "¿Tienes preguntas?")}
        </h2>
        <p className="text-[15px] text-redy-secondary mb-8 max-w-md mx-auto">
          {t(
            "Email us and we reply within one business day. No high-pressure sales.",
            "Escríbenos y te respondemos en un día hábil. Sin ventas de presión."
          )}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="mailto:hello@useredy.com"
            className="px-7 py-3.5 rounded-2xl text-[14px] font-bold text-white"
            style={{ background: GRADIENT_RESTAURANT }}
          >
            {t("Contact us", "Contáctanos")} →
          </a>
          <Link
            href="/signup"
            className="px-7 py-3.5 rounded-2xl text-[14px] font-bold text-redy-charcoal bg-redy-warm-white border border-black/5"
          >
            {t("Start free", "Empezar gratis")}
          </Link>
        </div>
      </div>
    </div>
  );
}
